import React, { useState, useEffect, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/header/Header';
import Footer from '../components/footer/Footer';
import SliderStyle3 from '../components/slider/SliderStyle3';
import 'react-tabs/style/react-tabs.css';
import parse from 'html-react-parser';
import styled from 'styled-components';
import emailjs from 'emailjs-com';
import { Helmet } from 'react-helmet';

// ✅ Styled shimmer loader
const LoaderWrapper = styled.div`
  width: 100%;
  height: 400px;
  margin: 20px 0;
`;

const Shimmer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background: linear-gradient(
    to right,
    #f0f0f0 0%,
    #e0e0e0 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const SliderWrapper = styled.div`
  padding-top: 20px;
`;

const LuxAccomodation = () => {
  const { id } = useParams();
  const [itemData, setItemData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    contactNumber: '',
    serviceType: '',
    email: '',
    message: '',
  });

  // Load Calendly script dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Fetch accommodation data
  useEffect(() => {
    setLoading(true);
    fetch(`https://web-production-1ab9.up.railway.app/api/one_bedroom/${id}/details/`)
      .then((response) => response.json())
      .then((data) => {
        console.log('✅ Accommodation response:', data);
        setItemData(data);
        setLoading(false);
        setFormData((prev) => ({
          ...prev,
          serviceType: data?.accomodation?.title || '',
        }));
      })
      .catch((error) => {
        console.error('❌ Error fetching accommodation:', error);
        setLoading(false);
      });
  }, [id]);

  // Handle form changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    setFormError('');
    emailjs
      .send('service_ptqtluk', 'template_uyicl9l', formData, 'apNJP_9sXnff2q82W')
      .then(() => setFormSubmitted(true))
      .catch((error) => console.log('Email error:', error));
  };

  // ✅ Safe image extraction with full guards
  const heroSliderData = (() => {
    if (!itemData || !Array.isArray(itemData.cover_photos)) {
      console.warn('⚠️ No cover_photos array found:', itemData);
      return [];
    }

    return itemData.cover_photos
      .filter((photo) => photo && typeof photo.cover_photos === 'string' && photo.cover_photos.trim() !== '')
      .map((photo) => ({
        src: photo.cover_photos,
      }));
  })();

  return (
    <div className="item-details">
      <Helmet>
        <title>
          {itemData?.accomodation?.title
            ? `${itemData.accomodation.title} | Luxury Accommodation in Cape Town`
            : "Don't miss out on this accommodation if you're in Cape Town"}
        </title>
        <meta
          name="description"
          content={
            itemData?.accomodation?.title
              ? `${itemData.accomodation.title} - click for more info`
              : 'Luxury accommodation in Cape Town.'
          }
        />
      </Helmet>

      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="flat-title-page inner">
        <div className="overlay"></div>
        <div className="themesflat-container">
          <div className="row">
            <div className="col-md-12">
              <center>
                <div className="page-title-heading mg-bt-12">
                  {loading ? (
                    <Shimmer width="250px" height="32px" radius="6px" />
                  ) : (
                    <h4
                      className="tf-title-heading ct style-2 fs-30 mg-bt-10"
                      style={{ color: 'white' }}
                    >
                      {itemData?.accomodation?.title || 'Accommodation Details'}
                    </h4>
                  )}
                </div>
              </center>
            </div>
          </div>
        </div>
      </section>

      {/* Slider Section */}
      <SliderWrapper>
        {loading ? (
          <LoaderWrapper>
            <Shimmer height="400px" radius="12px" />
          </LoaderWrapper>
        ) : heroSliderData.length > 0 ? (
          <SliderStyle3 data={heroSliderData} />
        ) : (
          <div
            style={{
              background: '#eee',
              height: '400px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              color: '#777',
            }}
          >
            No images available
          </div>
        )}
      </SliderWrapper>

      {/* Details Section */}
      <div className="tf-section tf-item-details">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="content-center">
                <div className="sc-item-details">
                  {loading ? (
                    <>
                      <Shimmer height="20px" width="60%" />
                      <br />
                      <Shimmer height="20px" width="90%" />
                      <br />
                      <Shimmer height="20px" width="80%" />
                      <br />
                      <Shimmer height="20px" width="70%" />
                    </>
                  ) : (
                    <div>{parse(itemData?.accomodation?.body || '')}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <br />

          {/* Contact Form */}
          <div className="tf-section tf-item-details">
            <div className="container">
              <div className="row">
                <div className="col-md-12">
                  <div className="content-center">
                    <div className="sc-item-details">
                      {formSubmitted ? (
                        <div className="thank-you-message">
                          <h2>Thank You!</h2>
                          <p>Your enquiry has been successfully submitted. We will get back to you soon.</p>
                        </div>
                      ) : (
                        <Fragment>
                          <h1 className="tf-title-heading ct style-2 fs-30 mg-bt-10">
                            Enquire Now
                          </h1>
                          <div className="form-inner">
                            <form id="contactform" noValidate onSubmit={handleSubmit}>
                              <div className="row">
                                <div className="col-md-6">
                                  <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    placeholder="Your Name"
                                    onChange={handleChange}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    placeholder="Your Email"
                                    onChange={handleChange}
                                  />
                                  {formError && <p style={{ color: 'red' }}>{formError}</p>}
                                </div>
                                <div className="col-md-12">
                                  <textarea
                                    name="message"
                                    value={formData.message}
                                    placeholder="Let us know your travel dates, preferences, and group size — we’d love to help!"
                                    onChange={handleChange}
                                  ></textarea>
                                </div>
                                <div className="col-md-12">
                                  <button type="submit" className="sc-button loadmore style fl-button pri-3">
                                    <span>Enquire Now</span>
                                  </button>
                                </div>
                              </div>
                            </form>
                          </div>
                        </Fragment>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LuxAccomodation;
